import React from 'react'
import {useIntl} from 'react-intl'
import styled from '@emotion/styled'
import {HeaderWithStore} from 'components/HeaderWithStore'
import {MetaType} from 'types/Meta'

import {Button} from '@vio/atlas/Button'
import {Box} from '@vio/atlas/helpers/Box'
import {NAV_BAR_MOBILE_HEIGHT} from '@vio/atlas/NavBar'
import {Overlay} from '@vio/atlas/Overlay'
import {Text} from '@vio/atlas/Text'
import {cssTheme} from '@vio/atlas/themes'
import {linariaMq} from '@vio/atlas/utils/breakpoints'
import {useBrandCapabilities} from '@vio/core/src/_web/brand/hooks/useBrandCapabilities'
import {
  FormattedMessageWrapper,
  FormattedMessageWrapperProps
} from '@vio/core/src/localization/components/FormattedMessage'

interface ErrorPresenterPropsType {
  buttonAction?: () => void
  imageURL?: string
  messages?: {
    buttonLabel?: FormattedMessageWrapperProps
    firstLine?: FormattedMessageWrapperProps
    secondLine?: FormattedMessageWrapperProps
    title?: FormattedMessageWrapperProps
  }
  metaParams?: MetaType
  showWasNotChargedMessage?: boolean
  supportButton?: {
    label: FormattedMessageWrapperProps
    url: string
  }
}

const ImageElement = styled('div')<{src: string}>`
  width: 100%;
  max-width: 874px;
  height: 250px;
  margin: 0 auto;
  background-position: center;
  background-image: ${({src}) => `url(${src})`};
  background-size: cover;

  ${linariaMq.desktopXs} {
    height: 350px;
  }
`

const Wrapper = styled('div')`
  height: calc(100% - ${NAV_BAR_MOBILE_HEIGHT}px);
  padding-top: ${cssTheme.layout.spacing.s400};
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`

const Title = styled('div')`
  margin-bottom: ${cssTheme.layout.spacing.s500};
`

const Message = styled('div')`
  color: ${cssTheme.colors.content.neutral.primary};
  margin-top: ${cssTheme.layout.spacing.s700};
`

const ErrorTextElement = styled('div')`
  margin: ${cssTheme.layout.spacing.s300} auto;
  max-width: 328px;
`

const Footer = styled('div')`
  text-align: center;
  max-width: 328px;
  margin: ${cssTheme.layout.spacing.s600} auto 0;
`

const FooterItem = styled('div')`
  margin-bottom: ${cssTheme.layout.spacing.s600};
`

export const ErrorPresenter = ({
  messages,
  buttonAction,
  showWasNotChargedMessage,
  supportButton,
  imageURL
}: ErrorPresenterPropsType) => {
  const {locale} = useIntl()
  const {auth} = useBrandCapabilities()

  return (
    <Overlay isOpen>
      <HeaderWithStore
        disableLogoClick
        currentLanguage={locale}
        onBackButtonClick={buttonAction}
        hasUserWidget={auth.showSignIn}
      />
      <Wrapper>
        <Message>
          <Box padding="s500">
            {messages?.title && (
              <Title>
                <Text as="h2" variant="titleL" align="center">
                  <FormattedMessageWrapper {...messages.title} />
                </Text>
              </Title>
            )}

            {showWasNotChargedMessage && (
              <ErrorTextElement>
                <Text align="center">
                  <FormattedMessageWrapper
                    id="noChargingMessage"
                    defaultMessage="Nothing was charged on your credit card."
                  />
                </Text>
              </ErrorTextElement>
            )}

            {messages?.firstLine && (
              <ErrorTextElement>
                <Text align="center">
                  <FormattedMessageWrapper {...messages.firstLine} />
                </Text>
              </ErrorTextElement>
            )}

            {messages?.secondLine && (
              <ErrorTextElement>
                <Text align="center">
                  <FormattedMessageWrapper {...messages.secondLine} />
                </Text>
              </ErrorTextElement>
            )}

            <Footer>
              {buttonAction && messages?.buttonLabel && (
                <FooterItem>
                  <Button
                    fullWidth
                    dataId="ButtonAction"
                    size="lg"
                    onClick={buttonAction}
                  >
                    <FormattedMessageWrapper {...messages.buttonLabel} />
                  </Button>
                </FooterItem>
              )}

              {supportButton && (
                <FooterItem>
                  <Text variant="bodyS" align="center">
                    <Button
                      fullWidth
                      dataId="SupportButton"
                      size="lg"
                      variant="tertiary"
                      onClick={() => window.open(supportButton.url)}
                    >
                      <FormattedMessageWrapper {...supportButton.label} />
                    </Button>
                  </Text>
                </FooterItem>
              )}
            </Footer>
          </Box>
        </Message>

        {imageURL && <ImageElement src={imageURL} />}
      </Wrapper>
    </Overlay>
  )
}

export default ErrorPresenter
